import { Grid } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useLocation, useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";

import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import useColors from "../../../../api/hooks/useColors";
import { controlKeys, entityKeys } from "../../../../api/queryKeys";
import { CustomerFeatureFlagsContext } from "../../../../contexts/CustomerFeatureFlagsContext";
import { DRAWER_TABS, ProtectionLevelText } from "../../../../utils/constants";
import AssetsSection from "../../controls/components/AssetsSection/AssetsSection";
import RelatedEntitiesSection from "../../controls/components/ControlMisconfiguration/relatedEntities/RelatedEntitiesSection";
import ControlRemediationDrawer from "../../controls/components/ControlRemediationDrawer/ControlRemediationDrawer";
import DetectionsAccordion from "../../detections/components/DetectionsAccordion";
import { getActiveDetections } from "../../detections/components/utils";
import { getStepRequestId } from "../utils";
import MissingControlsAlert from "./MissingControlsAlert";
import MitigationVulnerabilitiesTable from "./MitigationVulnerabilitiesTable";
import TabbedMitigationControlsTable from "./TabbedMitigationControlsTable";
import ThreatIntelAccordion from "./ThreatIntelAccordion";

const StepOverviewTab = ({
  selectedStep,
  shouldShowStep = true,
  entityTypeFilter = null,
  HeaderStep,
  handleViewDetections = () => {},
  assetAffectedByDetection = [],
  loading = false,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedControl, setSelectedControl] = useState(null);
  const [hasShownNotFoundToast, setHasShownNotFoundToast] = useState(false);
  const colors = useColors();
  const location = useLocation();
  const { detectionsActive } = useContext(CustomerFeatureFlagsContext);
  const [showDetailedAffectedEntities, setShowDetailedAffectedEntities] =
    useState(false);
  const { fetchControlsBatch, trackEvent, fetchEntitiesBatch } = useAPI();

  const { data: mitigatingControls, isLoading: mitigatingControlsLoading } =
    useQuery({
      queryKey: controlKeys.batchControls(
        selectedStep?.mitigating_controls_ids
      ), //the prefix key need to be the same of the key used in the allControls query for invalidating the cache
      queryFn: async () => {
        const controls = await fetchControlsBatch(
          selectedStep?.mitigating_controls_ids
        );
        return controls;
      },
      select: (response) => response.data,
      enabled: selectedStep !== null,
    });

  useEffect(() => {
    if (selectedStep) {
      // NOTE: this track message is monitored by Mixpanel to collect data on user engagement - DO NOT CHANGE IT
      trackEvent(`User opened drawer for step id ${selectedStep.step_id}`);
    }
  }, [selectedStep]);

  useEffect(() => {
    const control_id = searchParams.get("control_id");

    if (control_id !== null && mitigatingControls?.length > 0) {
      if (!Number.isNaN(Number(control_id))) {
        let control = mitigatingControls.find(
          (control) => control.control_id === Number(control_id)
        );
        if (control) {
          // means that the gap was found
          setSelectedControl(control);
          setHasShownNotFoundToast(false);
        } else if (!hasShownNotFoundToast) {
          // means that the gap was not found
          Toast.error("No control found with the given ID: " + control_id);
          setHasShownNotFoundToast(true);
        }
      } else if (!hasShownNotFoundToast) {
        // If the control_id is not a number, it is invalid
        Toast.error("Invalid param: " + control_id + "");
        setHasShownNotFoundToast(true);
      }
    } else {
      setSelectedControl(null);
    }
  }, [location, mitigatingControls, hasShownNotFoundToast]);

  useEffect(() => {
    if (mitigatingControls && selectedControl) {
      const control = mitigatingControls.find(
        (control) => control.control_id === selectedControl.control_id
      );
      if (control) {
        setSelectedControl(control);
      }
    }
  }, [mitigatingControls]);

  const onRowClickControl = (control_id) => {
    searchParams.set("control_id", encodeURIComponent(control_id));
    setSearchParams(searchParams.toString());
  };

  const onRowClickVulnerability = (row) => {
    window.open(
      `/posture/vulnerabilities?cve=${row.row.vulnerability_id}`,
      "_blank"
    );
    trackEvent("User open Vulnerability Drawer");
  };

  const onDrawerClose = () => {
    setSelectedControl(null);
    searchParams.delete("control_id");
    setSearchParams(searchParams.toString());
  };

  const { data: complexMisconfiguredEntitiesByType } = useQuery({
    queryKey: entityKeys.entitiesByStep(selectedStep?.step_id),
    queryFn: async () => {
      const entities = (
        await fetchEntitiesBatch(
          selectedStep?.misconfigured_entity_ids,
          getStepRequestId(selectedStep?.step_id)
        )
      ).data;
      // Filter entities by type for vulnerability steps if needed
      if (entityTypeFilter) {
        return entities.filter(
          (entity) => entity.entity_type === entityTypeFilter
        );
      }
      return entities;
    },
    select: (response) => {
      const entities = response;
      const entitiesByType = {};
      entities.forEach((entity) => {
        if (entity.entity_type in entitiesByType) {
          entitiesByType[entity.entity_type].push(entity);
        } else {
          entitiesByType[entity.entity_type] = [entity];
        }
      });
      return entitiesByType;
    },
    enabled: selectedStep !== null,
  });

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: colors.grey_50,
        height: "100%",
      }}
    >
      {loading && <LoadingIcon size={16} />}

      {selectedStep && (
        <>
          {!shouldShowStep && (
            <Typography variant="h6" mb={2}>
              Controls
            </Typography>
          )}
          {shouldShowStep && !loading && (
            <>
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  backgroundColor: colors.grey_50,
                  pt: 3,
                }}
              >
                <HeaderStep />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  p: "0 8px",
                }}
              >
                {detectionsActive &&
                  selectedStep?.is_detected &&
                  !showDetailedAffectedEntities && (
                    <DetectionsAccordion
                      detections={getActiveDetections(
                        selectedStep?.last_detections
                      )}
                      handleOnClick={handleViewDetections}
                      source={"step"}
                      assetAffectedByDetection={assetAffectedByDetection}
                    />
                  )}
                {selectedStep?.protection_level ===
                  ProtectionLevelText.MISSING_CONTROLS &&
                  !showDetailedAffectedEntities && (
                    <MissingControlsAlert
                      protectionCategory={selectedStep?.protection_category}
                    />
                  )}
                {!showDetailedAffectedEntities && (
                  <ThreatIntelAccordion step={selectedStep} />
                )}

                {selectedStep?.protection_level !==
                  ProtectionLevelText.MISSING_CONTROLS && (
                  <>
                    {!showDetailedAffectedEntities && (
                      <AssetsSection
                        entity_ids={selectedStep?.misconfigured_entity_ids}
                        source="step"
                      />
                    )}
                    {complexMisconfiguredEntitiesByType ? (
                      <RelatedEntitiesSection
                        complexEntitiesByType={
                          complexMisconfiguredEntitiesByType
                        }
                        showDetailedAffectedEntities={
                          showDetailedAffectedEntities
                        }
                        setShowDetailedAffectedEntities={
                          setShowDetailedAffectedEntities
                        }
                      />
                    ) : null}
                  </>
                )}
              </Box>
              {/* Mitigating Vulnerabilities/Controls */}
              {selectedStep?.cwe_id && !showDetailedAffectedEntities && (
                <Grid container mt={4} direction={"column"}>
                  <Typography mb={1} variant="subtitle1">
                    {shouldShowStep && "Related Vulnerabilities"}
                  </Typography>
                  <MitigationVulnerabilitiesTable
                    vulnerabilityIds={selectedStep?.related_vulnerability_ids}
                    onRowClick={onRowClickVulnerability}
                    title={""}
                  />
                </Grid>
              )}
              {selectedStep?.protection_level !==
                ProtectionLevelText.MISSING_CONTROLS &&
                !showDetailedAffectedEntities && (
                  <Grid container mt={4} direction={"column"}>
                    <Typography mb={1} variant="subtitle1">
                      {shouldShowStep && "What controls mitigate this risk?"}
                    </Typography>
                    {!mitigatingControlsLoading ? (
                      mitigatingControls?.length > 0 ? (
                        <TabbedMitigationControlsTable
                          mitigatingControls={mitigatingControls}
                          loading={mitigatingControlsLoading}
                          onRowClick={(control_id) => {
                            onRowClickControl(control_id);
                          }}
                          boxSXProps={{
                            border: "0px",
                          }}
                          filterColumns={[
                            "is_detected",
                            "control_id",
                            "name_for_gap_state",
                            "name_for_correct_state",
                            "tool",
                            "nist_function",
                            "severity",
                            "affected_entity_counts_by_type",
                          ]}
                          affectedEntityIdsFilter={
                            selectedStep?.misconfigured_entity_ids
                          }
                          isGapsTabInitialValue={
                            selectedStep?.protection_level !==
                            ProtectionLevelText.OPTIMAL_PROTECTION
                          }
                        />
                      ) : (
                        <Typography variant="subtitle2">
                          No mitigating controls found
                        </Typography>
                      )
                    ) : (
                      <LoadingIcon size={16} />
                    )}
                  </Grid>
                )}
            </>
          )}
          {/* CONTROLS DRAWER */}
          <ControlRemediationDrawer
            open={selectedControl !== null}
            onClose={onDrawerClose}
            selectedTab={DRAWER_TABS.CONTROL_TAB}
            selectedControl={selectedControl}
          />
        </>
      )}
    </Box>
  );
};

export default StepOverviewTab;
